import React from 'react';
import { Box, Divider, Grid, Paper, Switch, Typography } from '@mui/material';
import { PermissionModuleConfig } from '../../config/permissions-config';

interface GenericPermissionBoxProps {
    config: PermissionModuleConfig;
    permissions: any;
    onChange: (moduleKey: string, actionKey: string, value: boolean) => void;
    display?: string;
}

export const GenericPermissionBox: React.FC<GenericPermissionBoxProps> = ({ config, permissions, onChange, display }) => {
    if (display === 'none') return null;

    const modulePermissions = permissions?.[config.key] || {};
    const hasGroups = config.actions.some((a) => !!a.group);

    const groupedActions = React.useMemo(() => {
        const groups = new Map<string, PermissionModuleConfig['actions']>();

        for (const actionItem of config.actions) {
            const group = actionItem.group || 'Geral';
            const list = groups.get(group) || [];
            list.push(actionItem);
            groups.set(group, list);
        }

        return Array.from(groups.entries()).map(([group, actions]) => ({ group, actions }));
    }, [config.actions]);

    return (
        <Paper elevation={0} sx={{ padding: 2, marginBottom: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
                {config.label}
            </Typography>

            {!hasGroups && (
                <Grid container spacing={3} alignItems="center">
                    {config.actions.map((actionItem) => (
                        <Grid item key={`${config.key}-${actionItem.action}`} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Switch
                                color="primary"
                                size="small"
                                checked={!!modulePermissions[actionItem.action]}
                                onChange={(e) => onChange(config.key as string, actionItem.action, e.target.checked)}
                                sx={{ marginRight: 1 }}
                            />
                            <Typography variant="body2" sx={{ color: '#555' }}>{actionItem.label}</Typography>
                        </Grid>
                    ))}
                </Grid>
            )}

            {hasGroups && (
                <Box>
                    {groupedActions.map((groupItem, index) => (
                        <Box key={`${config.key}-${groupItem.group}`} sx={{ mt: index === 0 ? 0 : 2 }}>
                            <Typography variant="overline" sx={{ color: '#666' }}>
                                {groupItem.group}
                            </Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={3} alignItems="center">
                                {groupItem.actions.map((actionItem) => (
                                    <Grid item key={`${config.key}-${actionItem.action}`} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Switch
                                            color="primary"
                                            size="small"
                                            checked={!!modulePermissions[actionItem.action]}
                                            onChange={(e) => onChange(config.key as string, actionItem.action, e.target.checked)}
                                            sx={{ marginRight: 1 }}
                                        />
                                        <Typography variant="body2" sx={{ color: '#555' }}>{actionItem.label}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
};
